import { useState, useMemo, useEffect } from 'react';
import {
  Receipt, FileText, AlertCircle, CheckCircle2, X, Edit3, ListPlus,
  ChevronDown, ChevronUp, ExternalLink, Sparkles, Image as ImageIcon, RefreshCw
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import StatusBadge from '../../components/ui/StatusBadge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Skeleton from '../../components/ui/Skeleton';
import { useShop } from '../../hooks/useShop';
import { useAuth } from '../../contexts/AuthContext';
import { useCodChecks, useCodChecksSummary } from '../../hooks/useVendorData';
import { api } from '../../api/client';
import DrivePagePreview from '../../components/ui/DrivePagePreview';

const STATUS_TONES = {
  PENDING_REVIEW: 'neutral',
  AUDIT_PASSED: 'success',
  AUDIT_FLAGGED: 'danger',
  AWAITING_VENDOR_PROFILE: 'action',
  AWAITING_DOC_REVIEW: 'insight',
  IGNORED: 'neutral',
};

const STATUS_LABELS = {
  PENDING_REVIEW: 'Pending review',
  AUDIT_PASSED: 'Audit passed',
  AUDIT_FLAGGED: 'Flagged',
  AWAITING_VENDOR_PROFILE: 'Vendor profile needed',
  AWAITING_DOC_REVIEW: 'Doc review',
  IGNORED: 'Ignored',
};

const FINDING_LABELS = {
  no_ro_number: 'No RO# on invoice',
  handwritten_ro_needs_review: 'Handwritten RO# — verify',
  atypical_inventory_item: 'Atypical for vendor',
  unknown_vendor_no_profile: 'No vendor profile',
  vendor_profile_pending_approval: 'Vendor profile pending approval',
  vendor_was_shop_name: 'AI extracted shop name as vendor',
  vendor_name_missing: 'Vendor not extracted',
  cost_mismatch: 'Cost mismatch',
  cost_mismatch_transmission: 'Cost mismatch (transmission)',
  missing_tax_or_shipping: 'May be missing tax/shipping',
  part_not_on_ro: 'Part not on RO',
  on_deleted_ro: 'On deleted RO',
  no_receive_date: 'No receive date',
  audit_query_failed: 'Audit query error',
  ro_not_found_in_tekmetric: 'RO# not in Tekmetric',
  po_not_found_in_tekmetric: 'PO# not in Tekmetric',
  invoice_parts_not_on_ro: 'Invoice parts not on RO',
  vendor_confidence_medium: 'Vendor confidence: medium',
  check_payment_mismatch: 'Check payment mismatch vs invoices (under/overpaid; core netting excluded)',
  missing_from_cover_sheet: 'Stub present in PDF but not on cover sheet — verify check is real',
  no_backing_invoices: 'No invoice pages found for this check',
  invoice_sum_mismatch: 'Invoices total ≠ check amount (legacy)',
  check_overpayment: 'Check paid MORE than invoices total (legacy)',
  invoices_exceed_check: 'Invoice total exceeds check (legacy)',
  po_no_parts: 'PO has no parts',
  sublet_no_ro_number: 'Sublet missing RO#',
  vendor_ignored: 'Vendor marked ignored',
};

const TABS = [
  { key: 'needs_action', label: 'Needs action', filter: 'AUDIT_FLAGGED,AWAITING_VENDOR_PROFILE,AWAITING_DOC_REVIEW' },
  { key: 'pending', label: 'Pending review', filter: 'PENDING_REVIEW' },
  { key: 'passed', label: 'Audit passed', filter: 'AUDIT_PASSED' },
  { key: 'ignored', label: 'Ignored', filter: 'IGNORED' },
  { key: 'all', label: 'All', filter: null },
];

export default function CodChecksPage() {
  const { currentShopId, currentShop, shops } = useShop();
  const { isAllShops } = useAuth();
  const [activeTab, setActiveTab] = useState('needs_action');
  const [reviewingCheck, setReviewingCheck] = useState(null);
  const [reassigningCheck, setReassigningCheck] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [triggering, setTriggering] = useState(false);
  // For all-shops users, default to showing ALL shops (no filter) — they can
  // narrow via the dropdown below. For scoped users, always filter to their shop.
  const [shopFilter, setShopFilter] = useState(isAllShops ? '' : (currentShopId || ''));

  const filterStatus = TABS.find((t) => t.key === activeTab)?.filter;
  const effectiveShopId = shopFilter || (isAllShops ? null : currentShopId);
  const { checks, loading, refresh } = useCodChecks({
    shopId: effectiveShopId,
    auditStatus: filterStatus,
  });
  const { summary, refresh: refreshSummary } = useCodChecksSummary(effectiveShopId);

  const totalDollars = useMemo(
    () => checks.reduce((s, c) => s + (c.amount_usd || 0), 0),
    [checks]
  );

  async function triggerScan() {
    // For triggering a scan, we need a specific shop — fall back to currentShop
    const targetShop = shops.find((s) => s.shop_id === Number(shopFilter)) || currentShop;
    if (!targetShop) {
      alert('Pick a shop first via the filter or top-bar.');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    setTriggering(true);
    try {
      await api.processDayAsync(targetShop.shop_id, targetShop.nickname || targetShop.name, today);
      setTimeout(() => { refresh(); refreshSummary(); }, 3000);
    } finally {
      setTriggering(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="COD Checks Queue"
        description={
          shopFilter
            ? `${shops.find(s => s.shop_id === Number(shopFilter))?.nickname || 'Shop'} · Extracted from end-of-day Drive scans`
            : 'All shops · Extracted from end-of-day Drive scans'
        }
        meta={
          summary && (
            <div className="flex gap-2 flex-wrap">
              {Object.entries(summary.by_status || {}).map(([status, count]) => (
                <Badge key={status} tone={STATUS_TONES[status]} size="sm">
                  {STATUS_LABELS[status] || status}: {count}
                </Badge>
              ))}
              {summary.needs_attention_dollars_usd > 0 && (
                <Badge tone="danger" size="sm">
                  ${summary.needs_attention_dollars_usd.toFixed(0)} needs attention
                </Badge>
              )}
              {summary.total === 0 && (
                <Badge tone="neutral" size="sm">No checks extracted yet</Badge>
              )}
            </div>
          )
        }
        actions={
          <Button
            variant="primary"
            leftIcon={<Sparkles size={16} />}
            onClick={triggerScan}
            loading={triggering}
          >
            Process today's scan
          </Button>
        }
      />

      {/* Shop filter (only relevant for all-shops users) */}
      {isAllShops && shops.length > 0 && (
        <div className="flex items-center gap-3 mb-4 text-[13px]">
          <span className="text-[var(--color-warm-500)] font-semibold uppercase text-[10px] tracking-wide">Shop:</span>
          <select
            value={shopFilter}
            onChange={(e) => setShopFilter(e.target.value)}
            className="h-9 px-3 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] text-[13px] font-semibold text-[var(--color-warm-800)] focus:outline-none focus:border-[var(--color-insight-500)]"
          >
            <option value="">All shops</option>
            {shops.map((s) => (
              <option key={s.shop_id} value={s.shop_id}>
                {s.shop_nickname || s.shop_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b border-[var(--color-warm-100)] overflow-x-auto">
        {TABS.map((t) => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 h-10 text-[13px] font-semibold border-b-2 transition-colors -mb-px whitespace-nowrap ${
                isActive
                  ? 'border-[var(--color-warm-900)] text-[var(--color-warm-900)]'
                  : 'border-transparent text-[var(--color-warm-500)] hover:text-[var(--color-warm-800)]'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} height={70} />)}
        </div>
      ) : checks.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <Receipt size={36} className="text-[var(--color-warm-400)] mx-auto mb-2" />
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
              No checks in this view
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
              {activeTab === 'needs_action'
                ? "Nothing flagged. Click 'Process today's scan' to pull new files."
                : 'Try a different tab or run a scan.'}
            </div>
          </div>
        </Card>
      ) : (
        <Card padded={false}>
          <div className="px-5 py-3 border-b border-[var(--color-warm-100)] flex items-center justify-between text-[12px] text-[var(--color-warm-500)]">
            <span>{checks.length} checks · ${totalDollars.toFixed(2)} total</span>
          </div>
          <ul className="divide-y divide-[var(--color-warm-100)]">
            {checks.map((c) => (
              <CheckRow
                key={c.id}
                check={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                onReview={() => setReviewingCheck(c)}
                onReassign={() => setReassigningCheck(c)}
              />
            ))}
          </ul>
        </Card>
      )}

      {reviewingCheck && (
        <ReviewModal
          check={reviewingCheck}
          onClose={() => setReviewingCheck(null)}
          onReviewed={() => {
            setReviewingCheck(null);
            refresh();
            refreshSummary();
          }}
        />
      )}

      {reassigningCheck && (
        <ReassignVendorModal
          check={reassigningCheck}
          onClose={() => setReassigningCheck(null)}
          onReassigned={() => {
            setReassigningCheck(null);
            refresh();
            refreshSummary();
          }}
        />
      )}
    </div>
  );
}

function CheckRow({ check, expanded, onToggle, onReview, onReassign }) {
  const findings = check.audit_findings || [];
  const vp = check.vendor_profile;

  return (
    <li className="hover:bg-[var(--color-cream-100)] transition-colors">
      <div className="px-5 py-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-4">
          <button onClick={(e) => { e.stopPropagation(); onToggle(); }} className="text-[var(--color-warm-400)] hover:text-[var(--color-warm-700)]">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[13px] font-semibold text-[var(--color-warm-900)] mono-nums">
                #{check.check_number || '—'}
              </span>
              <span className="text-[13px] text-[var(--color-warm-700)]">
                {vp?.canonical_name || check.payee || '— unknown vendor —'}
              </span>
              {vp && (
                <Badge tone="cream" size="xs">{vp.vendor_category.replace('_', ' ')}</Badge>
              )}
              <StatusBadge
                status={check.audit_status.toLowerCase()}
                size="xs"
              >
                {STATUS_LABELS[check.audit_status] || check.audit_status}
              </StatusBadge>
              {check.cover_sheet_match ? (
                <Badge tone="success" size="xs">cover sheet ✓</Badge>
              ) : (check.audit_findings || []).includes('missing_from_cover_sheet') ? (
                <Badge tone="warning" size="xs">not on cover sheet</Badge>
              ) : null}
              {check.is_sublet && <Badge tone="insight" size="xs">sublet</Badge>}
            </div>
            <div className="text-[11px] text-[var(--color-warm-500)] flex items-center gap-2 flex-wrap">
              {check.invoices && check.invoices.length > 0 ? (
                check.invoices.length === 1 ? (
                  check.invoices[0].ro_number_extracted ? (
                    <span>RO: <span className="mono-nums">{check.invoices[0].ro_number_extracted}</span>{check.invoices[0].ro_number_handwritten && ' ✍'}</span>
                  ) : (
                    <span className="text-[var(--color-danger-600)]">No RO# on invoice</span>
                  )
                ) : (
                  <span><strong>{check.invoices.length} invoices</strong> · ROs: {check.invoices.map(i => i.ro_number_extracted || '—').join(', ').slice(0, 60)}</span>
                )
              ) : (
                <span className="text-[var(--color-danger-600)]">No backing invoices</span>
              )}
              <span>·</span>
              <span>{check.source_file_name}</span>
            </div>
            {findings.length > 0 && (
              <div className="mt-2 flex gap-1 flex-wrap">
                {findings.slice(0, 4).map((f, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 bg-[var(--color-danger-50)] text-[var(--color-danger-700)] rounded font-semibold">
                    {FINDING_LABELS[f] || f}
                  </span>
                ))}
                {findings.length > 4 && (
                  <span className="text-[10px] text-[var(--color-warm-500)]">+{findings.length - 4} more</span>
                )}
              </div>
            )}
          </div>

          <div className="text-right shrink-0">
            <div className="text-[14px] font-bold text-[var(--color-warm-900)] tabular-nums">
              ${check.amount_usd.toFixed(2)}
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onReassign(); }} title="Wrong vendor? Reassign + system learns">
            <RefreshCw size={13} />
          </Button>
          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); onReview(); }}>
            Review
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 ml-7 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="text-[12px] text-[var(--color-warm-700)] space-y-3">
            <div className="space-y-1">
              <div><strong>Folder date:</strong> {check.folder_date?.split('T')[0]}</div>
              <div><strong>From cover sheet:</strong> {check.payee || '—'} · ${check.amount_usd.toFixed(2)}</div>
              {vp && <div><strong>Vendor profile:</strong> {vp.canonical_name} ({vp.vendor_category})</div>}
            </div>

            {/* Backing invoices */}
            {check.invoices && check.invoices.length > 0 ? (
              <div>
                <div className="font-bold text-[var(--color-warm-900)] text-[13px] mb-2">
                  Backing invoices ({check.invoices.length})
                </div>
                <div className="space-y-2">
                  {check.invoices.map((inv) => {
                    const invFindings = inv.audit_findings || [];
                    return (
                      <div
                        key={inv.id}
                        className={`p-2.5 rounded-[var(--radius-sm)] border text-[12px] ${
                          inv.audit_status === 'AUDIT_PASSED'
                            ? 'bg-[var(--color-success-50)] border-[var(--color-success-100)]'
                            : inv.audit_status === 'AUDIT_FLAGGED'
                            ? 'bg-[var(--color-danger-50)] border-[var(--color-danger-100)]'
                            : 'bg-[var(--color-cream-100)] border-[var(--color-warm-100)]'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[var(--color-warm-500)] text-[10px] font-bold uppercase">Page {inv.page_index + 1}</span>
                            {inv.vendor_invoice_number && (
                              <span className="mono-nums text-[var(--color-warm-700)]">{inv.vendor_invoice_number}</span>
                            )}
                          </div>
                          <span className="font-semibold text-[var(--color-warm-900)] tabular-nums">${inv.amount_usd.toFixed(2)}</span>
                        </div>
                        <div className="text-[var(--color-warm-700)]">
                          RO/PO: <span className="mono-nums font-semibold">{inv.ro_number_extracted || '—'}</span>
                          {inv.ro_number_handwritten && <span className="ml-1 text-[var(--color-warm-500)]">(handwritten)</span>}
                        </div>
                        {invFindings.length > 0 && (
                          <div className="mt-1 flex gap-1 flex-wrap">
                            {invFindings.map((f, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 bg-white/60 text-[var(--color-warm-700)] rounded font-semibold">
                                {FINDING_LABELS[f] || f}
                              </span>
                            ))}
                          </div>
                        )}
                        {inv.line_items && inv.line_items.length > 0 && (
                          <details className="mt-1.5">
                            <summary className="cursor-pointer text-[10px] text-[var(--color-warm-500)] uppercase font-bold tracking-wide">
                              {inv.line_items.length} line item{inv.line_items.length !== 1 ? 's' : ''}
                            </summary>
                            <ul className="mt-1 ml-3 space-y-0.5">
                              {inv.line_items.slice(0, 8).map((li, i) => (
                                <li key={i} className="text-[11px]">
                                  · {li.description} {li.part_number && <span className="mono-nums text-[var(--color-warm-500)]">[{li.part_number}]</span>} · ${(li.line_total || li.unit_price || 0).toFixed(2)}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    );
                  })}
                  {check.invoice_count > 0 && check.invoice_sum_cents > 0 && (
                    <div className="text-[11px] text-[var(--color-warm-500)] tabular-nums px-2">
                      Sum of invoices: ${(check.invoice_sum_cents / 100).toFixed(2)}
                      {Math.abs(check.invoice_sum_cents - check.amount_cents) > 100 && (
                        <span className="ml-2 text-[var(--color-danger-600)] font-semibold">
                          ≠ check ${check.amount_usd.toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-[var(--radius-md)] bg-[var(--color-action-50)] border border-[var(--color-action-100)] text-[12px] text-[var(--color-action-700)]">
                <strong>No invoice pages found.</strong> The cover sheet listed this check but no backing invoice was identified in the scan. Use the preview to verify.
              </div>
            )}

            {findings.length > 0 && (
              <div className="pt-2 border-t border-[var(--color-warm-100)]">
                <strong className="text-[11px] uppercase text-[var(--color-warm-500)]">Roll-up findings:</strong>
                <ul className="ml-4 list-disc mt-1">
                  {findings.map((f, i) => <li key={i}>{FINDING_LABELS[f] || f}</li>)}
                </ul>
              </div>
            )}
            {check.notes && (
              <div className="pt-2 border-t border-[var(--color-warm-100)]">
                <strong>Notes:</strong> {check.notes}
              </div>
            )}
          </div>
          <DrivePagePreview
            fileId={check.source_drive_file_id}
            initialPage={check.invoices?.[0]?.page_index ?? (check.page_index >= 0 ? check.page_index : 0)}
            label="Invoice scan (arrows to flip pages)"
          />
        </div>
      )}
    </li>
  );
}

function ReassignVendorModal({ check, onClose, onReassigned }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetId, setTargetId] = useState('');
  const [learnAlias, setLearnAlias] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/api/vendor-profiles?shop_id=${check.shop_id}&approval_status=APPROVED`)
      .then((d) => { setProfiles(d.profiles || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [check.shop_id]);

  async function submit() {
    if (!targetId) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.reassignCheckVendor(check.id, {
        target_vendor_profile_id: Number(targetId),
        learn_alias: learnAlias,
        rerun_audit: true,
      });
      onReassigned();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const filteredProfiles = profiles.filter((p) => p.id !== check.vendor_profile?.id);

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Reassign vendor"
      description={`Currently matched to: ${check.vendor_profile?.canonical_name || '(none)'} — extracted payee was "${check.payee || '?'}"`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={submitting} disabled={!targetId}>
            Reassign + Re-audit
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="p-3 rounded-[var(--radius-md)] bg-[var(--color-insight-50)] border border-[var(--color-insight-100)] text-[12px] text-[var(--color-insight-700)]">
          The system will <strong>learn this mapping</strong> — next time the AI extracts "{check.payee}" from a scan, it'll auto-match to the vendor you pick here. Stop the same mistake from repeating.
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">
            Correct vendor
          </label>
          {loading ? (
            <Skeleton height={40} />
          ) : (
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full h-10 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 text-[14px]"
              autoFocus
            >
              <option value="">— Select a vendor profile —</option>
              {filteredProfiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.canonical_name} ({p.vendor_category})
                </option>
              ))}
            </select>
          )}
          <p className="text-[11px] text-[var(--color-warm-500)] mt-1.5">
            Don't see the right vendor? <a href="/auditor/vendors" className="underline">Create a new profile</a>.
          </p>
        </div>

        <label className="flex items-center gap-2 text-[13px] text-[var(--color-warm-700)]">
          <input
            type="checkbox"
            checked={learnAlias}
            onChange={(e) => setLearnAlias(e.target.checked)}
            className="w-4 h-4"
          />
          <span>Add "{check.payee}" as an alias of the chosen vendor (recommended)</span>
        </label>

        {error && (
          <div className="p-3 rounded-[var(--radius-md)] bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[13px] text-[var(--color-danger-700)]">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}


function ReviewModal({ check, onClose, onReviewed }) {
  const [decision, setDecision] = useState('AUDIT_PASSED');
  const [notes, setNotes] = useState('');
  const [createTask, setCreateTask] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await api.reviewCodCheck(check.id, {
        audit_status: decision,
        notes: notes || null,
        create_task: createTask,
      });
      onReviewed();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Review check #${check.check_number || '—'} for $${check.amount_usd.toFixed(2)}`}
      description={`${check.vendor_profile?.canonical_name || check.payee || 'Unknown vendor'}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={submitting}>Save decision</Button>
        </>
      }
    >
      <div className="space-y-4">
        {check.audit_findings?.length > 0 && (
          <div className="p-3 rounded-[var(--radius-md)] bg-[var(--color-action-50)] border border-[var(--color-action-100)]">
            <div className="text-[12px] font-semibold text-[var(--color-action-700)] mb-1">AI flagged:</div>
            <ul className="text-[12px] text-[var(--color-warm-700)] space-y-0.5">
              {check.audit_findings.map((f, i) => (
                <li key={i}>· {FINDING_LABELS[f] || f}</li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-2">Decision</label>
          <div className="space-y-2">
            {[
              { value: 'AUDIT_PASSED', label: 'Audit passed', desc: "Everything checks out — no further action." },
              { value: 'AUDIT_FLAGGED', label: 'Flag for follow-up', desc: 'Real issue — task needed for store user.' },
              { value: 'IGNORED', label: 'Ignore', desc: 'Not relevant (personal expense, false positive, etc.).' },
            ].map((opt) => (
              <label key={opt.value} className={`flex gap-3 p-3 rounded-[var(--radius-md)] border cursor-pointer transition-colors ${
                decision === opt.value
                  ? 'border-[var(--color-warm-900)] bg-[var(--color-cream-50)]'
                  : 'border-[var(--color-warm-100)] hover:border-[var(--color-warm-200)]'
              }`}>
                <input
                  type="radio"
                  name="decision"
                  value={opt.value}
                  checked={decision === opt.value}
                  onChange={(e) => setDecision(e.target.value)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-[13px] font-semibold text-[var(--color-warm-900)]">{opt.label}</div>
                  <div className="text-[11px] text-[var(--color-warm-500)]">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {decision === 'AUDIT_FLAGGED' && (
          <label className="flex items-center gap-2 text-[13px] text-[var(--color-warm-700)] p-3 bg-[var(--color-cream-100)] rounded-[var(--radius-md)]">
            <input
              type="checkbox"
              checked={createTask}
              onChange={(e) => setCreateTask(e.target.checked)}
              className="w-4 h-4"
            />
            <span>Create a task and notify the store user</span>
          </label>
        )}

        <div>
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Add context for the store user or your future self…"
            className="w-full bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] p-3 text-[13px]"
          />
        </div>
      </div>
    </Modal>
  );
}
